import type { EnrichedFeaturePermission } from "@dealer-center/ddc-pages-configuration-interface";

/**
 * Sentinel key for the group that holds every row which resolved neither a `groupName` nor a
 * `groupId` (FR-033). A constant rather than an enum (Configuration Principle III), and deliberately
 * not a value CC9 could ever send: the legacy `"global"` id is reserved for the Administrator entry
 * and no permission code maps to it.
 */
export const CATCH_ALL_GROUP_KEY = "__catch_all__";

export interface PermissionRow {
  /** `EnrichedFeaturePermission.name` — CC9's code, and the label fallback. */
  code: string;
  /** `displayName ?? code`. Never blank (FR-033). */
  label: string;
  /** The row's own `granted` flag. Never inferred (FR-032). */
  isGranted: boolean;
}

export interface PermissionGroup {
  key: string;
  label: string;
  isCatchAll: boolean;
  rows: PermissionRow[];
}

/*
 * `description` is absent from this view model on purpose.
 *
 * FR-032 asks for "each permission's human-readable name and its description where one exists", and
 * data-model §2 lists `description: string | null` — but `EnrichedFeaturePermission` carries no such
 * field, and neither does the CC9 response the BFF enriches (`{ name, access }`). So no description
 * ever exists, and a field that can only ever be null is noise that reads like an unimplemented
 * feature. The prototype shows descriptions only because its fixture data invents them.
 *
 * Recorded rather than worked around: if descriptions are wanted, they need a Label Services key
 * (`permission.resource.<name>.description` or similar) and BFF enrichment — a change to the lambda,
 * not to this file.
 */

const nonBlank = (value: string | null | undefined) =>
  value && value.trim() ? value : undefined;

/**
 * Turns the flat `permissions[]` payload into the grouped view model the tab renders.
 *
 * Grouping key is `groupName ?? groupId ?? catch-all` (data-model §2). Preferring the resolved name
 * over the id is safe as a *partition* key because `groupName` is derived deterministically from
 * `groupId` (Label Services `permission.group.<groupId>`), so both partition the rows identically —
 * and it means the key is already the string worth displaying.
 *
 * Row order **within** a group is payload order, which the BFF set deliberately: the account catalog
 * as CC9 returned it, then any granted code the catalog omitted. Groups are ordered named-first
 * (alphabetical), catch-all last.
 *
 * @param catchAllLabel Localized label for the catch-all group. Passed in rather than resolved here
 * so this stays a pure function — Configuration Principle VI still applies, it is just enforced at
 * the call site, which is also what makes the unit test independent of `labels.json`.
 */
export const groupPermissions = (
  permissions: EnrichedFeaturePermission[],
  catchAllLabel: string
): PermissionGroup[] => {
  const groups = new Map<string, PermissionGroup>();

  permissions.forEach(permission => {
    const key =
      nonBlank(permission.groupName) ??
      nonBlank(permission.groupId) ??
      CATCH_ALL_GROUP_KEY;
    const isCatchAll = key === CATCH_ALL_GROUP_KEY;

    let group = groups.get(key);
    if (!group) {
      group = {
        key,
        label: isCatchAll ? catchAllLabel : key,
        isCatchAll,
        rows: []
      };
      groups.set(key, group);
    }

    group.rows.push({
      code: permission.name,
      // FR-033: a row whose name cannot be resolved shows its raw code, never a blank row. For a
      // real account this fallback is the majority path, not the edge case.
      label: nonBlank(permission.displayName) ?? permission.name,
      // FR-032: read the row's own flag. NOT its presence (every catalog row is present, granted or
      // not) and NOT `access`, which is null on an ungranted row and would read as falsy by accident.
      isGranted: permission.granted
    });
  });

  /*
   * `Array.from(map.values())`, never `[...map.values()]`. The repo typechecks at the ES5 default —
   * `tsconfig.base.json` sets no `target` and no `downlevelIteration` — so spreading a Map iterator
   * is a hard **TS2802** error. It would not fail `test`, because ts-jest uses its own transform;
   * only `:typecheck` runs `tsc --build` (research R-011, and T038 is the explicit check).
   */
  return Array.from(groups.values()).sort((left, right) => {
    if (left.isCatchAll !== right.isCatchAll) {
      return left.isCatchAll ? 1 : -1;
    }
    return left.label.localeCompare(right.label);
  });
};
